<template>
	<article class="d-news-list-custom">
		<Component
			:is="PageHero"
			:title="header.title"
			:description="header.description"
			v-bind="{ breadcrumb }"
		/>

		<input
			v-for="(item, index) in searchFilters.fields.filter(
				(e) => e.type === 'Hidden'
			)"
			:key="`hidden-${index}`"
			type="hidden"
			:name="item.name"
			:value="item.value"
		/>

		<div class="bg-primary pb-xl px-layout-margin">
			<div v-if="computedFilters.type" class="mb-md">
				<BaseInputRadio
					:value="computedFilterValues.type"
					:config="computedFilters['type']"
					@input="(event) => updateValue('type', event)"
				/>
			</div>

			<div
				:class="[
					'flex flex-wrap flex-col gap-x-0 gap-y-sm mb-lg',
					'>=1024:flex-row >=1024:gap-x-layout-gutter',
				]"
			>
				<BaseInputDropdown
					v-if="computedFilters.month"
					:class="{
						'w-full min-w-fit >=1024:w-2col flex-grow':
							computedFilters.category,
						'w-full min-w-fit >=1024:w-3col flex-grow':
							!computedFilters.category,
					}"
					:value="computedFilterValues.month"
					:config="computedFilters.month"
					:aria-label="computedFilters.month.placeholder"
					@input="(event) => updateValue('month', event)"
				/>
				<BaseInputDropdown
					v-if="computedFilters.year"
					:class="{
						'w-full min-w-fit >=1024:w-2col flex-grow':
							computedFilters.category,
						'w-full min-w-fit >=1024:w-3col flex-grow':
							!computedFilters.category,
					}"
					:value="computedFilterValues.year"
					:config="computedFilters.year"
					:aria-label="computedFilters.year.placeholder"
					@input="(event) => updateValue('year', event)"
				/>
				<BaseInputDropdown
					v-if="computedFilters.category"
					class="w-full min-w-fit >=1024:w-4col flex-grow"
					:value="computedFilterValues.category"
					:config="computedFilters.category"
					:aria-label="computedFilters.category.placeholder"
					@input="(event) => updateValue('category', event)"
				/>
				<BaseInputText
					v-model="textValue"
					:class="{
						'w-full >=1024:w-4col flex-grow':
							computedFilters.category,
						'w-full min-w-fit >=1024:w-3col flex-grow':
							!computedFilters.category,
					}"
					:placeholder="computedFilters.text.placeholder"
					@keypress.enter="() => updateValue('text', textValue)"
				>
					<template #button>
						<button
							:class="[
								'absolute top-1/2 right-8',
								'transform -translate-y-1/2',
								'rounded-full bg-extra w-40 h-40',
								'flex justify-center items-center',
							]"
							aria-label="Søg efter nyheder"
							@click="() => updateValue('text', textValue)"
						>
							<SvgSearch class="w-16 h-16 text-white" />
						</button>
					</template>
				</BaseInputText>
			</div>

			<LimboSearch
				v-slot="{ data, pagination, state, action }"
				:search-filters="filters"
				:config="{
					enableLiveSearch: true,
					allowSearchFiltersMutation: true,
				}"
				class="relative"
			>
				<Transition
					:duration="200"
					mode="out-in"
					name="t-news-list-custom__content"
				>
					<div v-if="state.isLoading" key="news-list-custom__loader">
						<SearchLoader class="mx-auto w-md text-extra" />
					</div>

					<div
						v-if="!state.isLoading"
						key="news-list-custom__content"
					>
						<div
							v-if="pagination && pagination.total === 0"
							class="text-center w-full col-start-1 col-end-5"
							v-text="'Ingen resultater fundet'"
						></div>

						<div v-if="data && data.length > 0" class="space-y-xl">
							<div
								v-for="(entry, entryIndex) in formatItems(data)"
								:key="entry[0]"
							>
								<BaseH2
									class="text-primary-button mb-sm"
									v-text="entry[0]"
								/>

								<div
									:class="[
										'd-news-list-custom__content',
										'grid grid-cols-1 >=768:grid-cols-2 >=1024:grid-cols-3 >=1440:grid-cols-4',
										'gap-x-layout-gutter gap-y-md',
									]"
								>
									<!-- Subscription tile/box -->
									<div
										v-if="
											showSubscriptionBox && !entryIndex
										"
										key="subscriptionLink"
										:class="[
											'd-news-list-custom_subscription-link',
											'bg-primary-button rounded-lg',
											'p-md text-white',
											'flex flex-col justify-between',
											'order-3 >=768:order-2 >=1024:order-3 >=1440:order-4',
										]"
									>
										<BaseH3
											v-if="subscriptionTitle"
											tag="div"
											v-text="subscriptionTitle"
										/>

										<div
											v-if="subscriptionText"
											:class="
												[
													subscriptionTitle &&
														'mt-xs',
													'whitespace-pre-wrap',
												].filter(Boolean)
											"
											v-text="subscriptionText"
										></div>

										<div
											v-if="
												subscriptionLink &&
												subscriptionLink[0]
											"
											class="mt-auto"
										>
											<NuxtLinkExt
												:to="subscriptionLink[0].url"
												:target="
													subscriptionLink[0].target
												"
												class="inline-block mt-md"
											>
												<BaseButton
													tag="div"
													:class="['w-fit']"
													outline="white-1"
												>
													<template #default>
														<span
															v-if="
																subscriptionLink[0]
															"
															v-text="
																subscriptionLink[0]
																	.name
															"
														></span>
													</template>

													<template #icon>
														<SvgArrow
															class="
																transform
																-rotate-45
															"
														/>
													</template>
												</BaseButton>
											</NuxtLinkExt>
										</div>
									</div>

									<!-- Desktop Card -->
									<ContentCard
										v-for="(item, index) in entry[1]"
										:key="`card-desktop-${index}`"
										:to="item.url"
										:target="item.target"
										:name="item.title"
										:image="{
											...(item.image || {}),
											aspectRatio: 1.5,
										}"
										class="hidden >=1024:block"
										:class="{
											'order-1': index === 0,
											'order-2': index === 1,
											'order-3': index === 2,
											'order-4': index >= 3,
										}"
										size="small"
										light
									>
										<template #after>
											<span
												v-text="
													formatDate(item.newsDate)
												"
											></span>
										</template>
									</ContentCard>

									<!-- Mobile Card -->
									<ContentCard
										v-for="(item, index) in entry[1]"
										:key="`card-mobile-${index}`"
										:to="item.url"
										:target="item.target"
										:name="item.title"
										class=">=1024:hidden"
										:class="{
											'order-1': index === 0,
											'order-2': index === 1,
											'order-3': index === 2,
											'order-4': index >= 3,
										}"
										size="medium"
										light
									>
										<template #before>
											<span
												v-text="
													item.pageType ===
													'PressReleasePage'
														? 'Pressemeddelelse'
														: 'Nyhed'
												"
											></span>
										</template>

										<template #after>
											<span
												v-text="
													formatDate(item.newsDate)
												"
											></span>
										</template>
									</ContentCard>
								</div>
							</div>

							<div
								v-if="
									pagination.total >
									pagination.limit + pagination.offset
								"
								class="w-full flex justify-center"
							>
								<button
									:class="[
										'flex items-center space-x-12 mx-auto',
										'uppercase text-button font-semibold',
										'w-fit text-text hover:text-primary-button',
										'group',
									]"
									@click="action.fetchMore"
								>
									<span
										:class="[
											'flex-shrink-0',
											'duration-200 ease-smooth-out',
										]"
										v-text="'Vis flere'"
									></span>

									<SvgCaret
										:class="[
											'w-12 transform rotate-90',
											'duration-200 ease-smooth-out',
											'group-hover:translate-y-2',
										]"
									/>
								</button>
							</div>
						</div>
					</div>
				</Transition>
			</LimboSearch>
		</div>

		<FeedbackForm v-if="activateFeedback" class="mb-lg" />
	</article>
</template>

<script>
import SvgSearch from '~/assets/svgs/search-icon.svg?inline';
import SvgCaret from '~/assets/svgs/caret-icon.svg?inline';
import LimboSearch from '~/citi-baseline/components/LimboSearch';
import ContentCard from '~/components/shared/ContentCard';
import SearchLoader from '~/components/shared/SearchLoader';
import FeedbackForm from '~/components/shared/FeedbackForm';

const MainPageHero = () => import('~/components/main/PageHero');
const SubsitePageHero = () => import('~/components/subsite/PageHero');

const months = [
	'Januar',
	'Februar',
	'Marts',
	'April',
	'Maj',
	'Juni',
	'Juli',
	'August',
	'September',
	'Oktober',
	'November',
	'December',
];

export default {
	name: 'SubsiteNewsListCustom',

	components: {
		SvgSearch,
		SvgCaret,
		LimboSearch,
		ContentCard,
		SearchLoader,
		FeedbackForm,
	},

	inheritAttrs: false,

	props: {
		header: {
			type: Object,
			required: true,
		},
		breadcrumb: {
			type: Array,
			required: false,
		},
		searchFilters: {
			type: Object,
			required: true,
		},
		activateFeedback: {
			type: Boolean,
			default: false,
		},
		showSubscriptionBox: {
			type: Boolean,
			default: false,
		},
		subscriptionTitle: {
			type: String,
			default: '',
		},
		subscriptionText: {
			type: String,
			default: '',
		},
		subscriptionLink: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			filters: this.searchFilters,
			textValue:
				this.searchFilters?.fields?.find((e) => e.name === 'text')
					?.value || '',
		};
	},

	computed: {
		PageHero() {
			const map = {
				main: MainPageHero,
				subsite: SubsitePageHero,
			};
			return map[this.$store.state.site.alias] || MainPageHero;
		},
		computedFilters() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = element;
			});

			return response;
		},
		computedFilterValues() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = Array.isArray(element.value)
					? element.value?.find?.((e) => e.checked)?.value
					: element.value;
			});

			return response;
		},
	},

	watch: {
		'computedFilterValues.text'(value) {
			this.textValue = value;
		},
	},

	methods: {
		updateValue(name, event) {
			const field = this.filters.fields.find((e) => e.name === name);

			if (field) {
				if (typeof event === 'string' && Array.isArray(field.value)) {
					field.value.forEach((element) => {
						element.checked = element.value === event;
					});
				} else if (typeof event === 'string') {
					field.value = event;
				} else {
					field.value = event?.target?.value ?? '';
				}
			}
		},

		formatDate(dateString) {
			const date = new Date(dateString);

			const day = `0${date.getDate()}`.slice(-2);
			const month = `0${date.getMonth() + 1}`.slice(-2);
			const year = date.getFullYear();

			return `${day}.${month}.${year}`;
		},

		formatItems(_items) {
			const items = _items.concat([]);
			items.sort((a, b) => {
				if (a.newsDate !== b.newsDate) {
					return (a.newsDate < b.newsDate) * 2 - 1;
				}

				return 0;
			});

			const response = {};
			items.forEach((item) => {
				const date = new Date(item.newsDate);
				const month = months[date.getMonth()];
				const year = date.getFullYear();
				const label = `${month} ${year}`;

				!response[label] && (response[label] = []);
				response[label].push(item);
			});

			return Object.entries(response);
		},
	},
};
</script>

<style lang="postcss">
.d-news-list-custom {
	@apply bg-primary;
}
.t-news-list-custom__content-enter-active,
.t-news-list-custom__content-leave-active {
	@apply duration-200 ease-smooth-out;
}

.t-news-list-custom__content-enter,
.t-news-list-custom__content-leave-to {
	@apply opacity-0;
}

/**
	Forces primary background on text,
	because SiteImprove is weird.
 */
.d-news-list-custom_subscription-link,
.d-news-list-custom_subscription-link * {
	color: #ffffff !important;
}
</style>
